import {
  FieldWrapper,
  type FieldWrapperInjectedProps,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import { cn } from "cn";
import type { ComponentProps, ReactNode } from "react";
import { BwCheckmarkLine, BwCloseCircleLine } from "@/components/ui/icons";
import { Input } from "@/components/ui/input/input";
import {
  type FieldBindingProps,
  PolyField,
  splitBinding,
} from "@/fields/_internal/poly-field";
import {
  isStrongPassword,
  PASSWORD_REQUIREMENTS,
} from "@/fields/text/password-schema";

type PasswordFieldBaseProps = Omit<
  ComponentProps<typeof Input>,
  "value" | "onChange" | "onBlur" | "onClear" | "type" | "className"
> & {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner Input. */
  inputClassName?: string;
  onBlur?: () => void;
  /**
   * Render the strength bar + requirement checklist under the input once the
   * user has typed something. Default `false` — use it on the field that SETS a
   * password, never on "confirm password" or on a sign-in password.
   */
  showStrength?: boolean;
};

export type PasswordFieldProps = PasswordFieldBaseProps & FieldBindingProps;

function PasswordStrength({ value }: { value: string }) {
  if (!value) return null;
  const isStrong = isStrongPassword(value);
  return (
    <div className="flex flex-col gap-2 mt-2">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            /* sorted */
            "overflow-hidden",
            "flex-1",
            "h-1.5",
            "rounded-full bg-border-dark-low",
          )}
        >
          <div
            className={cn(
              /* sorted */
              "h-full",
              "rounded-full",
              "transition-all",
              isStrong ? "w-full bg-bg-success-max" : "w-1/2 bg-bg-warning-max",
            )}
          />
        </div>
        <span
          className={cn(
            /* sorted */
            "text-xs",
            isStrong ? "text-text-success" : "text-text-warning",
          )}
        >
          {isStrong ? "Strong password" : "Weak password"}
        </span>
      </div>
      <div className="flex flex-col gap-1 text-sm">
        {PASSWORD_REQUIREMENTS.map((requirement) => {
          const met = requirement.test(value);
          return (
            <div key={requirement.label} className="flex items-center gap-2">
              {met ? (
                <BwCheckmarkLine
                  size={16}
                  className="text-text-success shrink-0"
                />
              ) : (
                <BwCloseCircleLine
                  size={16}
                  className="text-text-dark-low shrink-0"
                />
              )}
              <span
                className={met ? "text-text-success" : "text-text-dark-low"}
              >
                {requirement.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PasswordField(props: PasswordFieldProps) {
  const [binding, rest] = splitBinding(props);
  const {
    label,
    description,
    optional,
    className,
    inputClassName,
    onBlur,
    showStrength = false,
    ...inputProps
  } = rest;
  return (
    <PolyField<string> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={inputProps.disabled}
        >
          <PasswordControl
            inputProps={inputProps}
            inputClassName={inputClassName}
            showStrength={showStrength}
            value={field.state.value ?? ""}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}

/**
 * FieldWrapper cloneElement-injects id/aria-* into its single child. The child
 * here is input + strength meter, so this component receives the injection and
 * forwards it onto the real <input> — the label's htmlFor and the invalid
 * outline stay wired.
 */
function PasswordControl({
  inputProps,
  inputClassName,
  showStrength,
  value,
  onChange,
  onBlur,
  ...injected
}: FieldWrapperInjectedProps & {
  inputProps: Omit<
    ComponentProps<typeof Input>,
    "value" | "onChange" | "onBlur"
  >;
  inputClassName?: string;
  showStrength: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}) {
  return (
    <div>
      <Input
        {...inputProps}
        {...injected}
        type="password"
        className={inputClassName}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {showStrength && <PasswordStrength value={value} />}
    </div>
  );
}
